import type { User } from 'firebase/auth'

/**
 * Authentication states
 */
export enum AuthState {
  IDLE = 'idle',
  LOADING = 'loading',
  GOOGLE_SIGNIN = 'google-signin',
  VERIFYING = 'verifying',
  SENDING_TO_EXTENSION = 'sending-to-extension',
  AUTHENTICATED = 'authenticated',
  ERROR = 'error'
}

/**
 * Authentication modes
 */
export enum AuthMode {
  SIGNIN = 'signin',
  SIGNUP = 'signup'
}

/**
 * Firebase Auth error codes
 */
export enum FirebaseAuthErrorCode {
  POPUP_CLOSED = 'auth/popup-closed-by-user',
  ACCOUNT_EXISTS = 'auth/account-exists-with-different-credential',
  NETWORK_REQUEST_FAILED = 'auth/network-request-failed',
  TOO_MANY_REQUESTS = 'auth/too-many-requests',
  USER_CANCELLED = 'auth/user-cancelled',
  UNAUTHORIZED_DOMAIN = 'auth/unauthorized-domain'
}

/**
 * Extension message types
 */
export enum ExtensionMessageType {
  AUTH_TOKEN = 'AUTH_TOKEN'
}

/**
 * API endpoints
 */
export enum APIEndpoint {
  VERIFY_TOKEN = '/auth/verify-token',
  USER_SETTINGS = '/user/settings',
  DELETE_ACCOUNT = '/user/account',
  SNIPPETS = '/snippets',
  BILLING = '/user/billing',
  SUPPORT_TICKETS = '/support/tickets',
  SUPPORT_FEEDBACK = '/support/feedback',
  TOPUP_CHECKOUT = '/topup/checkout',
  TOPUP_HISTORY = '/topup/history',
  FEATURE_FLAGS = '/feature-flags',
  FEATURE_FLAGS_TOGGLE = '/feature-flags/toggle'
}

/**
 * Authentication context state
 */
export interface AuthContextState {
  user: User | null
  loading: boolean
  error: string | null
  state: AuthState
  isNewUser: boolean
}

/**
 * Authentication context actions
 */
export interface AuthContextActions {
  signInWithGoogle: () => Promise<void>
  signOut: () => Promise<void>
  clearError: () => void
  handleNewUserLogin: (firebaseUser: User) => Promise<boolean>
}

/**
 * Complete authentication context type
 */
export type AuthContext = AuthContextState & AuthContextActions

/**
 * Extension message structure
 */
export interface ExtensionMessage {
  type: ExtensionMessageType
  token: string
  closeTab?: boolean
}

/**
 * Extension response structure
 */
export interface ExtensionResponse {
  success: boolean
  message?: string
}

/**
 * API token verification request
 */
export interface TokenVerificationRequest {
  idToken: string
}

/**
 * API token verification response
 */
export interface TokenVerificationResponse {
  customToken: string
  user?: {
    uid: string
    email: string
    displayName?: string
  }
}

/**
 * Authentication error
 */
export interface AuthError {
  code: string
  message: string
  details?: unknown
}

/**
 * User settings interface
 */
export interface UserSettings {
  customInstructions?: string;
  maxToolCallLimit?: number;
}

/**
 * Snippet interface
 */
export interface Snippet {
  id: string;
  name: string;
  content: string;
  createdAt: string;
}

/**
 * Create snippet data
 */
export interface CreateSnippetData {
  name: string;
  content: string;
}

/**
 * Update snippet data
 */
export interface UpdateSnippetData {
  name?: string;
  content?: string;
}

export enum PlanType {
  FREE = "free",
  STARTER_MONTHLY = "starter_monthly",
  STARTER_YEARLY = "starter_yearly",
  PRO_MONTHLY = "pro_monthly",
  PRO_YEARLY = "pro_yearly",
  BUSINESS_MONTHLY = "business_monthly",
  BUSINESS_YEARLY = "business_yearly",
}

export enum BillingStatus {
  ACTIVE = "active",
  PAST_DUE = "past_due",
  CANCELED = "canceled",
  INCOMPLETE = "incomplete",
  TRIALING = "trialing",
}

/**
 * Billing data interface
 */
export interface BillingData {
  creditsRemaining: number;
  planType: PlanType;
  billingStatus: BillingStatus;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  hasActiveSubscription: boolean;
  hasTopupCredits?: boolean;
  topupCreditsRemaining?: number;
  topupExpirationDate?: string | null;
  trialEndsAt?: string | null;
}

/**
 * Topup purchase history item
 */
export interface TopupPurchaseHistory {
  id: string;
  creditsAdded: number;
  creditsRemaining: number;
  usdAmount: number;
  packageName: string;
  purchasedAt: string;
  expiresAt: string;
  status: 'active' | 'expired' | 'consumed';
}

/**
 * Support ticket data
 */
export interface CreateSupportTicketData {
  subject: string;
  description: string;
  category: string;
}

/**
 * Support feedback data (for uninstall feedback, no auth required)
 */
export interface CreateSupportFeedbackData {
  subject: string
  description: string
  firstName: string
  email: string
}

/**
 * Feature flag interface
 */
export interface FeatureFlag {
  id: string;
  name: string;
  description: string;
  enabledByDefault: boolean;
  enabled: boolean;
}

/**
 * Feature flags response
 */
export interface FeatureFlagsResponse {
  features: FeatureFlag[];
}

/**
 * Toggle feature flag request
 */
export interface ToggleFeatureFlagRequest {
  featureId: string;
  enabled: boolean;
}

/**
 * Extension communication callback types
 */
export type ExtensionSuccessCallback = () => void
export type ExtensionErrorCallback = (errorMessage: string) => void

/**
 * Check if error is a Firebase Auth error
 */
// TODO: wtf is this??
export function isFirebaseAuthError(error: unknown): error is { code: string; message: string } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    'message' in error &&
    typeof (error as { code: unknown }).code === 'string' &&
    typeof (error as { message: unknown }).message === 'string'
  )
}
